import React from "react"
import { Icon } from "../icon"
import type { IconName } from "../icon/assets/icon-map"
import {
  NavigationAccountType,
  NavigationServiceLevel,
  useNavigationContext,
  type NavigationContextValue
} from "./navigation-context"

export type NavigationBadgeType = "paid" | "pro" | "beta"

export const BADGE_ICONS: Record<NavigationBadgeType, IconName> = {
  paid: "diamond",
  pro: "diamond",
  beta: "beta-beaker"
}

/**
 * Whether a badge should be hidden for the current account. Both the inline
 * icon (`NavigationBadge`) and the tooltip callout (`NavigationTooltip`) gate
 * on this so the two stay in lockstep.
 */
export function isBadgeHidden(
  type: NavigationBadgeType,
  accountType: NavigationContextValue["accountType"],
  serviceLevel: NavigationContextValue["serviceLevel"]
): boolean {
  if (type === "paid") return accountType === NavigationAccountType.Paid
  if (type === "pro")
    return (
      accountType === NavigationAccountType.Paid &&
      serviceLevel === NavigationServiceLevel.CreatorPro
    )
  return false
}

/**
 * Inline badge icon rendered inside `Navigation.Item`, `Navigation.Group`,
 * and `Navigation.GroupItem` when a `badge` prop is set. Maps the semantic
 * type to the corresponding icon. Internal — used only via the `badge`
 * prop on the three navigation row primitives.
 */
export function NavigationBadge({ type }: { type: NavigationBadgeType }) {
  const { accountType, serviceLevel } = useNavigationContext()

  if (isBadgeHidden(type, accountType, serviceLevel)) return null

  return (
    <Icon
      name={BADGE_ICONS[type]}
      // eslint-disable-next-line rulesdir/no-deprecated-icon-size
      size="sm"
      className="shrink-0 text-gray-700"
    />
  )
}
